import React from "react";
import { RiAddLine, RiSearchLine, RiDeleteBin6Line } from "react-icons/ri";
import { formatTime } from "../../utils/date";
import type { Conversation } from "../../services/chatbotApi";

interface Props {
  isOpen: boolean;
  conversations: Conversation[];
  isLoadingConversations: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  activeConversationId: string | null;
  searchQuery: string;
  onNewChat: () => void;
  onSearchChange: (value: string) => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (e: React.MouseEvent, id: string) => void;
  onLoadMore: () => void;
}

// px from the bottom that triggers loading the next page.
const LOAD_MORE_THRESHOLD = 80;

const ChatSidebar: React.FC<Props> = ({
  isOpen,
  conversations,
  isLoadingConversations,
  isFetchingMore,
  hasMore,
  activeConversationId,
  searchQuery,
  onNewChat,
  onSearchChange,
  onSelectConversation,
  onDeleteConversation,
  onLoadMore,
}) => {
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetchingMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  };

  return (
  <div
    className={`flex flex-col border-r border-gray-100 bg-gray-50 overflow-hidden transition-all duration-300 ease-in-out ${
      isOpen ? "w-[240px] min-w-[240px]" : "w-0 min-w-0"
    }`}
  >
    <div className="px-4 pt-5 pb-3 border-b border-gray-100 shrink-0">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
        Conversations
      </p>
      <button
        onClick={onNewChat}
        className="w-full flex items-center justify-center gap-1.5 bg-[#090F21] text-white text-[11px] font-medium py-2 rounded-lg hover:bg-[#1a2540] transition-colors mb-2"
      >
        <RiAddLine size={13} />
        New Chat
      </button>
      <div className="relative">
        <RiSearchLine
          size={13}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search chats..."
          className="w-full bg-white rounded-lg py-2 pl-8 pr-3 text-[11px] font-medium border border-gray-200 outline-none focus:ring-1 focus:ring-[#090F21] placeholder:text-gray-400 transition-all"
        />
      </div>
    </div>

    <div className="flex-1 overflow-y-auto py-2" onScroll={handleScroll}>
      {isLoadingConversations ? (
        <div className="flex justify-center mt-8">
          <span className="w-4 h-4 border-2 border-gray-200 border-t-[#090F21] rounded-full animate-spin" />
        </div>
      ) : conversations.length === 0 ? (
        <p className="text-center text-[10px] text-gray-400 mt-8 px-4">
          No conversations yet.
          <br />
          Start chatting to get going!
        </p>
      ) : (
        <>
          {conversations.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelectConversation(item.id)}
            className={`w-full text-left px-4 py-2.5 transition-colors group border-b border-gray-50 last:border-0 ${
              activeConversationId === item.id ? "bg-white" : "hover:bg-white"
            }`}
          >
            <div className="flex items-center justify-between gap-1">
              <span
                className={`text-[11px] font-semibold truncate flex-1 ${
                  activeConversationId === item.id
                    ? "text-[#090F21]"
                    : "text-gray-600 group-hover:text-[#090F21]"
                }`}
              >
                {item.title}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[9px] text-gray-400">
                  {formatTime(item.updatedAt)}
                </span>
                <button
                  onClick={(e) => onDeleteConversation(e, item.id)}
                  aria-label="Delete conversation"
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 transition-all"
                >
                  <RiDeleteBin6Line size={11} />
                </button>
              </div>
            </div>
          </button>
          ))}
          {isFetchingMore && (
            <div className="flex justify-center py-3">
              <span className="w-4 h-4 border-2 border-gray-200 border-t-[#090F21] rounded-full animate-spin" />
            </div>
          )}
        </>
      )}
    </div>
  </div>
  );
};

export default ChatSidebar;
